import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import LogoutIcon from "@mui/icons-material/Logout";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FolderIcon from "@mui/icons-material/Folder";
import BarChartIcon from "@mui/icons-material/BarChart";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useThemeMode } from "@/context/ThemeContext";
import { useNotification } from "@/context/NotificationContext";
import { useAppSelector } from "@/store";
import { useApiErrorMessage } from "@/hooks/useApiErrorMessage";

export function Account() {
  const { t } = useTranslation();
  const getErrorMessage = useApiErrorMessage();
  const navigate = useNavigate();
  const { user, logout, deleteAccount } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const { notify } = useNotification();
  const { items: projects } = useAppSelector((s) => s.projects);
  const { items: metrics } = useAppSelector((s) => s.metrics);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const totalApiKeys = projects.reduce(
    (sum, p) => sum + (p.apiKeys?.length || 0),
    0,
  );

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleDelete = async () => {
    try {
      await deleteAccount();
      navigate("/");
    } catch (err) {
      notify(getErrorMessage(err), "error");
    }
    setDeleteOpen(false);
  };

  return (
    <Box sx={{ maxWidth: 700 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 700,
        }}
      >
        {t("account.title")}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: 4,
        }}
      >
        {t("account.subtitle")}
      </Typography>
      {/* Identity */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("account.identity")}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              {t("account.emailAddress")}
            </Typography>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
            >
              <TextField
                value={user?.email || ""}
                size="small"
                disabled
                sx={{ maxWidth: 300 }}
              />
              <Chip
                icon={<GoogleIcon />}
                label={t("account.googleConnected")}
                color="success"
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
      {/* UI Preferences */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("account.uiPreferences")}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
              }}
            >
              {t("account.appearance")}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                mb: 1,
                display: "block",
              }}
            >
              {t("account.appearanceDesc")}
            </Typography>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={() => toggleTheme()}
              size="small"
            >
              <ToggleButton value="light">{t("account.light")}</ToggleButton>
              <ToggleButton value="dark">{t("account.dark")}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </CardContent>
      </Card>
      {/* Security */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t("account.security")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                }}
              >
                {t("account.activeSession")}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("account.activeSessionDesc")}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
            >
              {t("account.signOut")}
            </Button>
          </Box>
        </CardContent>
      </Card>
      {/* Danger Zone */}
      <Card sx={{ border: "1px solid", borderColor: "error.main" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <WarningAmberIcon color="error" />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "error.main",
              }}
            >
              {t("account.dangerZone")}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            {t("account.deleteDesc")}
          </Typography>
          <Button
            variant="contained"
            color="error"
            onClick={() => setDeleteOpen(true)}
          >
            {t("account.deleteButton")}
          </Button>
        </CardContent>
      </Card>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0, display: "flex", minHeight: 400 }}>
          {/* Left - Impact Summary */}
          <Box
            sx={{
              flex: 1,
              bgcolor: "error.main",
              color: "white",
              p: 4,
              borderRadius: "12px 0 0 12px",
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: 700,
              }}
            >
              {t("account.deleteTitle")}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>
              {t("account.deleteDesc")}
            </Typography>
            <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", mb: 2 }} />
            <Typography variant="overline" sx={{ opacity: 0.7 }}>
              {t("account.whatYouLose")}
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <FolderIcon sx={{ color: "white" }} />
                </ListItemIcon>
                <ListItemText
                  primary={t("account.deleteImpactProjects", {
                    count: projects.length,
                  })}
                  slotProps={{ primary: { sx: { fontWeight: 600 } } }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <BarChartIcon sx={{ color: "white" }} />
                </ListItemIcon>
                <ListItemText
                  primary={t("account.deleteImpactMetrics", {
                    count: metrics.length,
                  })}
                  slotProps={{ primary: { sx: { fontWeight: 600 } } }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <VpnKeyIcon sx={{ color: "white" }} />
                </ListItemIcon>
                <ListItemText
                  primary={t("account.deleteImpactApiKeys", {
                    count: totalApiKeys,
                  })}
                  slotProps={{ primary: { sx: { fontWeight: 600 } } }}
                />
              </ListItem>
            </List>
          </Box>

          {/* Right - Confirmation */}
          <Box sx={{ flex: 1, p: 4 }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: 700,
              }}
            >
              {t("account.deleteConfirmTitle")}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 2,
              }}
            >
              {t("account.deleteConfirmDesc")}
            </Typography>
            <Alert severity="error" sx={{ mb: 3 }}>
              {t("account.deleteWarning")}
            </Alert>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t("account.deleteVerify")}{" "}
              <strong>{t("account.deletePhrase")}</strong>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={t("account.deletePhrase")}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              sx={{ mb: 3 }}
            />
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setDeleteOpen(false);
                  setConfirmText("");
                }}
              >
                {t("account.cancel")}
              </Button>
              <Button
                variant="contained"
                color="error"
                disabled={confirmText !== t("account.deletePhrase")}
                onClick={handleDelete}
              >
                {t("account.deleteButton")}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
